import { stringSimilarity } from 'string-similarity-js';

/**
 * Resolve an entry's display label.
 * Priority: customName > label > resolved metadataId > placeholder.
 * (updateEntryIdentity deletes entry.label once a metadataId is assigned, so
 * label and metadataId are mutually exclusive in practice.)
 */
export function resolveEntryLabel(entry, store) {
  if (entry.customName) {
    return entry.customName;
  }
  if (entry.label) {
    return entry.label;
  }
  if (entry.metadataId) {
    const metadataName = store.getMetadataName(entry.metadataId);
    return metadataName || `[ID: ${entry.metadataId.substring(0, 8)}...]`;
  }
  return '—';
}

/**
 * Normalize entry name for matching.
 *
 * IMPORTANT: This logic MUST match the backend implementation in:
 * api/helpers/entryMetadata.js (normalizeEntryName function)
 *
 * Any changes to normalization logic must be applied to both
 * frontend and backend to ensure consistent matching.
 */
export function normalizeEntryName(str) {
  if (!str || typeof str !== 'string') {
    return '';
  }

  return str
    .trim()
    .toLowerCase()
    .normalize('NFD')                    // Decompose combined characters
    .replace(/[\u0300-\u036f]/g, '')     // Remove diacritics (accents)
    .replace(/\s+/g, ' ')                 // Collapse multiple spaces to single space
    .replace(/[^\w\s]/g, '')              // Remove punctuation and special characters
    .trim();
}

/**
 * Find the best matching metadata entry for a given label using string similarity
 * @param {string} label - The label to match
 * @param {Array} metadataList - Array of metadata objects with { id, name, kind }
 * @param {string} entryKind - The kind of entry ('income' or 'expense')
 * @param {number} threshold - Minimum similarity score (0-1) to consider a match (default: 0.7)
 * @returns {Object|null} Best matching metadata with similarityScore or null if no good match
 */
export function findBestMetadataMatch(label, metadataList, entryKind, threshold = 0.7) {
  if (!label || !label.trim()) return null;
  if (!metadataList || metadataList.length === 0) return null;

  // Filter by kind
  const filteredList = metadataList.filter(m => m.kind === entryKind);
  if (filteredList.length === 0) return null;

  const normalizedLabel = label.toLowerCase().trim();

  let bestMatch = null;
  let bestScore = 0;

  for (const meta of filteredList) {
    const score = stringSimilarity(
      normalizedLabel,
      meta.name.toLowerCase().trim()
    );

    if (score > bestScore) {
      bestScore = score;
      bestMatch = meta;
    }
  }

  // Return match only if it meets the threshold
  if (bestMatch && bestScore >= threshold) {
    return {
      ...bestMatch,
      similarityScore: bestScore
    };
  }

  return null;
}
